import React from 'react';

import AddDatasetToAnalysisConfigurationModal, {
  AddDatasetToAnalysisConfiguration,
} from 'analysis-tool/AddDatasetToAnalysisConfigurationModal';
import * as AnalysisActions from 'analysis-tool/AnalysisActions';
import {CreateAnalysisParams} from 'analysis-tool/AnalysisDAO';
import {isAnalysisEnabled} from 'analysis-tool/AnalysisToolFeatureGates';
import {CreateAnalysisModal} from 'analysis-tool/create-analysis-modal/CreateAnalysisModal';
import {getPersistedRunQueryData} from 'analysis-tool/data-frame-creation/DataFrameCreationUtils';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {AnalyticsParameterValue} from 'insights/parameters/__generated/AnalyticsParameterValue';
import {
  ActionType,
  CreateDatasetButton,
} from 'notebook/structured-tables/action-functions/createDatasetActionFunction';
import {AddOrReplaceAndRunStepParams} from 'pipelines/network/PipelineDAO';
import legacyRunModal from 'ui-library/legacyRunModal';

import {AnalyticsParameterConfigGraphQL} from '../../../../graphql-fragments/insights/__generated/AnalyticsParameterConfigGraphQL';

export interface AddBlockToAnalysisButtonProps {
  analyticsQueryId: string;
  analyticsQueryName: string;
  sqlQuery: string;
  dashboardId: string;
  parameterConfigs: Array<AnalyticsParameterConfigGraphQL>;
  parameterValues: Array<AnalyticsParameterValue>;
  disabled: boolean;
}

function buildStepParams(
  props: AddBlockToAnalysisButtonProps,
  dataFrameName: string
): AddOrReplaceAndRunStepParams {
  const persistedRunQueryData = getPersistedRunQueryData({
    parameterConfigs: props.parameterConfigs,
    parameterValues: props.parameterValues,
    sqlQuery: props.sqlQuery,
  });

  return {
    stepType: PipelineStepType.INITIALIZE_DATASET,
    stepVersion: 2 as const,
    stepConfig: {
      dataFrameName,
      dashboardId: props.dashboardId,
      blockId: props.analyticsQueryId,
      ...persistedRunQueryData,
    },
  };
}

function handleClickActionButton(props: AddBlockToAnalysisButtonProps, actionType: ActionType): void {
  async function handleAddToNewAnalysis(
    params: CreateAnalysisParams,
    dataFrameName: string
  ): Promise<{analysisId: string}> {
    const {analysisId} = await AnalysisActions.createAnalysis(params);

    try {
      await AnalysisActions.addOrReplaceAndRunAnalysisStep(analysisId, buildStepParams(props, dataFrameName));
      FileBrowserActions.openFile(analysisId);
      return {analysisId};
    } catch (error) {
      AnalysisActions.archiveAnalysis(
        analysisId,
        'Dataset could not be added to newly created Analysis',
        null
      );
      throw error;
    }
  }

  async function handleAddToExistingAnalysis(
    params: AddDatasetToAnalysisConfiguration
  ): Promise<{analysisId: string}> {
    await AnalysisActions.addOrReplaceAndRunAnalysisStep(
      params.analysisId,
      buildStepParams(props, params.dataFrameName)
    );
    FileBrowserActions.openFile(params.analysisId);
    return {analysisId: params.analysisId};
  }

  if (actionType === ActionType.NEW_ANALYSIS) {
    legacyRunModal(CreateAnalysisModal, {
      title: 'Add to new analysis',
      onCreateAnalysis: handleAddToNewAnalysis,
      actionOrigin: ActionOrigin.INSIGHTS,
      viaExistingTableProps: {initialDatasetName: props.analyticsQueryName},
    });
  } else {
    legacyRunModal(AddDatasetToAnalysisConfigurationModal, {
      onCreateDataFrame: handleAddToExistingAnalysis,
      initialDatasetName: props.analyticsQueryName,
    });
  }
}

export default function AddBlockToAnalysisButton(props: AddBlockToAnalysisButtonProps): JSX.Element | null {
  if (!isAnalysisEnabled()) {
    return null;
  }

  return (
    <CreateDatasetButton
      disabled={props.disabled}
      handleClickActionButton={(actionType) => {
        handleClickActionButton(props, actionType);
      }}
    />
  );
}
